// invoiceItemFromProduct.js
//
// Create INVOICE_ITEM nodes from PRODUCT catalog records and relate them to a Invoice.

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getResolvedFields } from 'utilities/NodeType';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { setNodeDocs } from 'services/nodes/NodeWriteService';
import {
  computeMarginPercent,
  computeInvoiceItemLineTotal,
} from 'nodetypes/invoice/view/invoiceItemsModel';
import {
  INVOICE_ITEM_TYPE_KEY,
  INVOICE_TYPE_KEY,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';

export const PRODUCT_TYPE_KEY = 'PRODUCT';

const COPY_FIELD_NAMES = Object.freeze([
  'sku',
  'manufacturer',
  'vendor',
  'unit',
  'leadTime',
  'category',
]);

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeRelationKeys(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeString(entry)).filter(Boolean);
  }
  const single = normalizeString(value);
  return single ? [single] : [];
}

function readProductField(product, fieldName) {
  if (!product || !fieldName) return undefined;
  const fromHelper = readNodeFieldRawValue(
    product,
    { name: fieldName, source: 'product' },
    PRODUCT_TYPE_KEY,
  );
  if (fromHelper !== undefined) return fromHelper;
  const section = product?.data_product;
  if (section && typeof section === 'object' && fieldName in section) {
    return section[fieldName];
  }
  return undefined;
}

function resolveItemSectionKey(itemType, fieldName) {
  const fields = Array.isArray(itemType?.schema?.fields)
    ? itemType.schema.fields
    : [];
  const resolved = getResolvedFields(INVOICE_ITEM_TYPE_KEY, { includeHidden: true }) || [];
  const all = [...fields, ...resolved];
  const fieldDef = all.find((field) => normalizeString(field?.name || field?.key) === fieldName);
  const source = normalizeString(
    fieldDef?.source || itemType?.key || INVOICE_ITEM_TYPE_KEY,
  ).toLowerCase();
  if (itemType?.dataSection && (!fieldDef?.source || fieldDef.source === itemType.key)) {
    return itemType.dataSection;
  }
  return source
    ? `data_${source}`
    : (itemType?.dataSection || 'data_invoice_item');
}

/**
 * Build a INVOICE_ITEM node payload from a PRODUCT (does not persist).
 *
 * Product unitPrice seeds Cost (buy). Unit Price (sell) starts equal (0% markup)
 * until the designer adjusts.
 *
 * @param {object} options
 * @param {object} options.product
 * @param {object} options.invoiceNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.itemType]
 * @returns {Promise<object>}
 */
export async function buildInvoiceItemFromProduct({
  product,
  invoiceNode,
  user,
  baseKey = null,
  itemType = null,
}) {
  const item = await createNodeFromFactory(INVOICE_ITEM_TYPE_KEY, user, baseKey || null);
  const productTitle = normalizeString(product?.title) || 'Untitled product';
  item.title = productTitle;

  const subtitle = normalizeString(product?.subtitle);
  if (subtitle) item.subtitle = subtitle;

  if (product?.coverUrl) item.coverUrl = product.coverUrl;
  if (product?.cover != null) item.cover = product.cover;

  item.parentTitle = normalizeString(invoiceNode?.title);
  item.parentType = invoiceNode?.type || INVOICE_TYPE_KEY;
  item.profileRoot = false;
  item.contributorKey = user?.key || null;

  const sectionWrites = {};
  const writeField = (fieldName, value) => {
    if (value === undefined) return;
    const sectionKey = resolveItemSectionKey(itemType, fieldName);
    const existing = sectionWrites[sectionKey] || (
      item[sectionKey] && typeof item[sectionKey] === 'object'
        ? { ...item[sectionKey] }
        : {}
    );
    existing[fieldName] = value;
    sectionWrites[sectionKey] = existing;
  };

  COPY_FIELD_NAMES.forEach((fieldName) => {
    const value = readProductField(product, fieldName);
    if (value === undefined || value === null || value === '') return;
    if (fieldName === 'vendor') {
      const keys = normalizeRelationKeys(value);
      if (!keys.length) return;
      writeField(fieldName, keys);
      return;
    }
    writeField(fieldName, value);
  });

  const productKey = normalizeString(product?.key);
  if (productKey) writeField('product', [productKey]);

  const quantity = 1;
  writeField('quantity', quantity);

  const cost = readProductField(product, 'unitPrice');
  if (cost !== undefined && cost !== null && cost !== '') {
    writeField('cost', cost);
  }

  const unitPrice = cost;
  const markupPercent = 0;
  if (unitPrice != null && unitPrice !== '') {
    writeField('unitPrice', unitPrice);
  }
  writeField('markupPercent', markupPercent);

  const marginPercent = computeMarginPercent(cost, unitPrice);
  if (marginPercent != null) writeField('marginPercent', marginPercent);

  const total = computeInvoiceItemLineTotal(quantity, unitPrice);
  if (total != null) writeField('total', total);

  writeField('taxable', true);
  writeField('status', 'pending');

  Object.entries(sectionWrites).forEach(([sectionKey, values]) => {
    item[sectionKey] = values;
  });

  return stampExclusiveAncestry(
    item,
    invoiceNode,
    itemType || getNodeType(INVOICE_ITEM_TYPE_KEY),
  );
}

/**
 * Create INVOICE_ITEM nodes from products and edge them to the Invoice.
 *
 * @param {object} options
 * @param {object[]} options.products
 * @param {object} options.invoiceNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.itemType]
 * @returns {Promise<object[]>}
 */
export async function createInvoiceItemsFromProducts({
  products,
  invoiceNode,
  user,
  baseKey = null,
  itemType = null,
}) {
  const list = (Array.isArray(products) ? products : []).filter((row) => row?.key);
  if (!list.length) return [];
  if (!invoiceNode?.key || !user?.key) {
    throw new Error('Invoice and user are required to create line items.');
  }

  const resolvedBaseKey = baseKey || invoiceNode.baseKey || null;
  const entries = [];

  for (const product of list) {
    const item = await buildInvoiceItemFromProduct({
      product,
      invoiceNode,
      user,
      baseKey: resolvedBaseKey,
      itemType,
    });
    entries.push({ key: item.key, payload: item });
  }

  await setNodeDocs(entries, { merge: false });

  for (const entry of entries) {
    await upsertEdge(
      invoiceNode.key,
      entry.key,
      buildHierarchyEdgeOptions({
        parentNode: invoiceNode,
        childNode: entry.payload,
        baseKey: resolvedBaseKey || null,
      }),
    );
  }

  return entries.map((entry) => entry.payload);
}
